import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const VISITOR_ID_KEY = "uda_visitor_id";
const VISIT_COUNT_KEY = "uda_visit_count";
const SESSION_ID_KEY = "uda_session_id";

const generateVisitorId = () => {
  return `v_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return "mobile";
  return "desktop";
};

const getSource = () => {
  const referrer = document.referrer;
  if (!referrer) return "Direct";
  
  const referrerUrl = new URL(referrer);
  const domain = referrerUrl.hostname;
  
  if (domain.includes("google")) return "Google";
  if (domain.includes("facebook") || domain.includes("fb.")) return "Facebook";
  if (domain.includes("instagram")) return "Instagram";
  if (domain.includes("twitter") || domain.includes("x.com")) return "Twitter/X";
  if (domain.includes("linkedin")) return "LinkedIn";
  if (domain.includes("youtube")) return "YouTube";
  if (domain.includes("tiktok")) return "TikTok";
  
  return referrerUrl.hostname;
};

export const useVisitorTracking = () => {
  const location = useLocation();
  const sessionIdRef = useRef<string | null>(null);
  const pageStartTimeRef = useRef<number>(Date.now());
  const currentPathRef = useRef<string>("");

  useEffect(() => {
    const initSession = async () => {
      try {
        // Get or create visitor ID
        let visitorId = localStorage.getItem(VISITOR_ID_KEY);
        let visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || "0", 10);
        const isReturning = !!visitorId;

        if (!visitorId) {
          visitorId = generateVisitorId();
          localStorage.setItem(VISITOR_ID_KEY, visitorId);
        }

        // Check if we have an active session
        let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
        
        if (!sessionId) {
          // New session
          visitCount++;
          localStorage.setItem(VISIT_COUNT_KEY, visitCount.toString());

          const { data, error } = await supabase
            .from("visitor_sessions")
            .insert({
              visitor_id: visitorId,
              is_returning: isReturning,
              visit_count: visitCount,
              source: getSource(),
              referrer: document.referrer || null,
              user_agent: navigator.userAgent,
              device_type: getDeviceType(),
            })
            .select("id")
            .single();

          if (error) {
            console.error("Error creating session:", error);
            return;
          }

          sessionId = data.id;
          sessionStorage.setItem(SESSION_ID_KEY, sessionId);
        }

        sessionIdRef.current = sessionId;
      } catch (error) {
        console.error("Error initializing tracking:", error);
      }
    };

    initSession();

    // Update session duration on page unload
    const handleUnload = async () => {
      if (sessionIdRef.current) {
        const startedAt = sessionStorage.getItem("session_start_time");
        const duration = startedAt 
          ? Math.floor((Date.now() - parseInt(startedAt, 10)) / 1000)
          : 0;

        navigator.sendBeacon(
          `https://wrghlmsinvhwvvfhhrvj.supabase.co/rest/v1/visitor_sessions?id=eq.${sessionIdRef.current}`,
          JSON.stringify({
            ended_at: new Date().toISOString(),
            duration_seconds: duration,
          })
        );
      }
    };

    sessionStorage.setItem("session_start_time", Date.now().toString());
    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  // Track page views
  useEffect(() => {
    const trackPageView = async () => {
      if (!sessionIdRef.current) {
        // Wait a bit for session to initialize
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      if (!sessionIdRef.current) return;

      // Calculate time on previous page
      const timeOnPrevPage = Math.floor((Date.now() - pageStartTimeRef.current) / 1000);
      
      // Update previous page time if exists
      if (currentPathRef.current && timeOnPrevPage > 0) {
        // We'll skip updating old page views for simplicity
      }

      // Track new page view
      const visitorId = localStorage.getItem(VISITOR_ID_KEY);
      if (!visitorId) return;

      try {
        await supabase.from("page_views").insert({
          session_id: sessionIdRef.current,
          visitor_id: visitorId,
          page_path: location.pathname,
          page_title: document.title,
        });

        // Update session page count directly
        const { data: sessionData } = await supabase
          .from("visitor_sessions")
          .select("pages_viewed")
          .eq("id", sessionIdRef.current)
          .single();

        if (sessionData) {
          await supabase
            .from("visitor_sessions")
            .update({ pages_viewed: (sessionData.pages_viewed || 0) + 1 })
            .eq("id", sessionIdRef.current);
        }
      } catch (error) {
        console.error("Error tracking page view:", error);
      }

      pageStartTimeRef.current = Date.now();
      currentPathRef.current = location.pathname;
    };

    trackPageView();
  }, [location.pathname]);
};

export default useVisitorTracking;
